import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Users, TrendingUp, ArrowUpRight, ArrowDownRight, Minus, Activity,
  ArrowLeft, Target, AlertTriangle, CheckCircle, ChevronDown,
  BarChart3, Shield, Plus,
} from 'lucide-react';
import { VUKZ, VUKZ_GRADIENT } from '../theme/vukzTheme';
import PageHeader from '../components/layout/PageHeader';
import AddAthleteModal from '../components/roster/AddAthleteModal';
import { getCategoryDetail, getAthletesByCategory } from '../services/managerPerformanceService';
import { getGestorDashboard } from '../services/gestorService';
import { useActiveContext } from '../contexts/ActiveContextContext';
import { useRBAC } from '../contexts/RBACContext';

// ─── Reusable ─────────────────────────────────────────────────────────────────

const WidgetCard = ({ icon: Icon, title, subtitle, children, className = '' }) => (
  <div className={`bg-white rounded-2xl border overflow-hidden ${className}`} style={{ borderColor: VUKZ.border }}>
    <div className="px-5 pt-5 pb-4">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: VUKZ_GRADIENT }}>
          <Icon size={18} color="white" />
        </div>
        <div>
          <h2 className="text-sm font-extrabold" style={{ color: VUKZ.text }}>{title}</h2>
          {subtitle && <p className="text-[10px]" style={{ color: VUKZ.textLight }}>{subtitle}</p>}
        </div>
      </div>
    </div>
    <div className="px-5 pb-5">{children}</div>
  </div>
);

const StatusDot = ({ status }) => {
  const color = status === 'improving' ? VUKZ.success : status === 'declining' ? VUKZ.error : VUKZ.textLight;
  return <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />;
};

const SkeletonRows = ({ count = 3 }) => (
  <div className="space-y-2">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="h-16 rounded-xl animate-pulse" style={{ backgroundColor: VUKZ.primaryLight }} />
    ))}
  </div>
);

// Pure helpers — sem dependências externas
const calcAge = (birthDate) => {
  if (!birthDate) return null;
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// ═══════════════════════════════════════════════════════════════════════════════
// Category Detail Page
// ═══════════════════════════════════════════════════════════════════════════════

const CategoryDetailPage = () => {
  const { categoryId } = useParams();
  const { clubId: activeClubId } = useActiveContext();
  const { clubId: rbacClubId } = useRBAC();
  // TODO(SEC-CONFLATION): mesmo caveat de CategoriesPage e ManagerHomePage —
  // rbacClubId vem de user_roles.club_id (JWT), mas services esperam institution_id.
  // Ver SECURITY_BACKLOG.md → SEC-CONFLATION e SEC-ACTIVECONTEXT-BOOTSTRAP.
  const effectiveClubId = activeClubId || rbacClubId;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [athletes, setAthletes] = useState([]);
  const [athletesLoading, setAthletesLoading] = useState(true);
  const [showAddAthlete, setShowAddAthlete] = useState(false);
  const [athletesRefresh, setAthletesRefresh] = useState(0);

  useEffect(() => {
    if (!effectiveClubId) {
      console.warn('[CategoryDetailPage] effectiveClubId=null (sem clube no ActiveContext nem no JWT) — nada a carregar.');
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        // Fix regressão do Ciclo 1a (commit 8300935): getCategoryDetail passou a exigir planId.
        // Busca o plano ativo via dashboard (mesmo padrão de ManagerHomePage:449
        // e GestorPlanPage:503).
        const dashboard = await getGestorDashboard(effectiveClubId);
        const planId = dashboard?.plan_id;
        if (!planId) {
          console.warn('[CategoryDetailPage] Sem plano ativo para clube ' + effectiveClubId + ' — categoria abriria sem diretrizes.');
          setLoading(false);
          return;
        }
        const detail = await getCategoryDetail(categoryId, effectiveClubId, planId);
        setData(detail);
      } catch (err) {
        console.error('Error loading category detail:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [categoryId, effectiveClubId]);

  // Fetch de atletas — separado do main data, não bloqueia a página se falhar
  useEffect(() => {
    if (!effectiveClubId || !categoryId) {
      setAthletesLoading(false);
      return;
    }
    let cancelled = false;
    const loadAthletes = async () => {
      setAthletesLoading(true);
      try {
        const rows = await getAthletesByCategory(effectiveClubId, categoryId);
        if (!cancelled) setAthletes(rows);
      } catch (err) {
        console.error('[CategoryDetailPage] athletes error:', err);
      } finally {
        if (!cancelled) setAthletesLoading(false);
      }
    };
    loadAthletes();
    return () => { cancelled = true; };
  }, [categoryId, effectiveClubId, athletesRefresh]);

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
          <SkeletonRows count={6} />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: VUKZ.bg }}>
        <div className="text-center">
          <AlertTriangle size={32} className="mx-auto mb-3" style={{ color: VUKZ.textLight }} />
          <p className="text-sm font-bold" style={{ color: VUKZ.text }}>Categoria não encontrada</p>
          <Link to="/manager/categories" className="text-xs mt-2 inline-block" style={{ color: VUKZ.primary }}>← Voltar</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: VUKZ.bg }}>
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">

        {/* Back + Header */}
        <Link to="/manager/categories" className="inline-flex items-center gap-1.5 text-xs font-bold mb-4 hover:opacity-70 transition-opacity" style={{ color: VUKZ.textLight }}>
          <ArrowLeft size={14} /> Categorias
        </Link>

        <PageHeader
          title={data.name}
          subtitle={`${data.age_range} · ${data.athlete_count} atletas · ${data.goals}`}
          badgeText="CATEGORIA"
          className="mb-6"
        />

        {/* KPI Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[
            { label: 'Readiness', value: data.readiness_avg?.toFixed(1), color: data.readiness_avg >= 7.5 ? VUKZ.success : data.readiness_avg >= 6.5 ? VUKZ.warning : VUKZ.error },
            { label: 'Carga (km/sem)', value: data.weekly_load?.volume_km, color: VUKZ.primary },
            { label: 'Aderência', value: `${data.adherence_pct}%`, color: data.adherence_pct >= 85 ? VUKZ.success : data.adherence_pct >= 70 ? VUKZ.warning : VUKZ.error },
            { label: 'Monotonia', value: data.weekly_load?.monotony, color: data.weekly_load?.monotony > 1.5 ? VUKZ.warning : VUKZ.textLight },
            { label: 'Riscos', value: data.risk_count, color: data.risk_count > 0 ? VUKZ.error : VUKZ.success },
          ].map((kpi) => (
            <div key={kpi.label} className="bg-white rounded-xl border p-3 text-center" style={{ borderColor: VUKZ.border }}>
              <div className="text-lg font-black" style={{ color: kpi.color }}>{kpi.value}</div>
              <div className="text-[10px]" style={{ color: VUKZ.textLight }}>{kpi.label}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Widget 1: Timeline Semanal (Planejado vs Executado) */}
          <WidgetCard icon={BarChart3} title="Timeline Semanal" subtitle="Planejado vs executado — últimas 4 semanas">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ color: VUKZ.textLight }}>
                    <th className="text-left py-2 font-semibold">Semana</th>
                    <th className="text-center py-2 font-semibold">Plan (km)</th>
                    <th className="text-center py-2 font-semibold">Exec (km)</th>
                    <th className="text-center py-2 font-semibold">Δ</th>
                    <th className="text-center py-2 font-semibold">Sessões</th>
                    <th className="text-center py-2 font-semibold">Readiness</th>
                    <th className="text-center py-2 font-semibold">Int</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.weeks || []).map((w) => {
                    const delta = ((w.executed_km - w.planned_km) / w.planned_km * 100).toFixed(0);
                    const deltaColor = Math.abs(delta) <= 5 ? VUKZ.success : Math.abs(delta) <= 15 ? VUKZ.warning : VUKZ.error;
                    return (
                      <tr key={w.week} className="border-t" style={{ borderColor: VUKZ.border }}>
                        <td className="py-2.5 font-bold" style={{ color: VUKZ.text }}>{w.week}</td>
                        <td className="text-center py-2.5" style={{ color: VUKZ.textLight }}>{w.planned_km}</td>
                        <td className="text-center py-2.5 font-bold" style={{ color: VUKZ.text }}>{w.executed_km}</td>
                        <td className="text-center py-2.5">
                          <span className="text-[10px] font-bold" style={{ color: deltaColor }}>
                            {delta > 0 ? '+' : ''}{delta}%
                          </span>
                        </td>
                        <td className="text-center py-2.5" style={{ color: VUKZ.text }}>
                          {w.executed_sessions}/{w.planned_sessions}
                        </td>
                        <td className="text-center py-2.5">
                          <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full" style={{
                            backgroundColor: `${w.readiness >= 7.5 ? VUKZ.success : w.readiness >= 6.5 ? VUKZ.warning : VUKZ.error}15`,
                            color: w.readiness >= 7.5 ? VUKZ.success : w.readiness >= 6.5 ? VUKZ.warning : VUKZ.error,
                          }}>
                            {w.readiness}
                          </span>
                        </td>
                        <td className="text-center py-2.5" style={{ color: VUKZ.textLight }}>{w.intensity}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </WidgetCard>

          {/* Widget 2: Distribuição de Carga */}
          <WidgetCard icon={Activity} title="Distribuição de Carga" subtitle="Zonas de treinamento — período atual">
            {data.load_distribution && (
              <div className="space-y-4">
                {[
                  { zone: 'Aeróbico (Z1–Z2)', pct: data.load_distribution.aerobic, color: '#22c55e', target: '60–80%' },
                  { zone: 'Limiar (Z3)', pct: data.load_distribution.threshold, color: '#f59e0b', target: '15–25%' },
                  { zone: 'Velocidade (Z4–Z5)', pct: data.load_distribution.speed, color: '#ef4444', target: '5–15%' },
                ].map((z) => (
                  <div key={z.zone}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-semibold" style={{ color: VUKZ.text }}>{z.zone}</span>
                      <span className="text-[10px]" style={{ color: VUKZ.textLight }}>Alvo: {z.target}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-3 rounded-full" style={{ backgroundColor: VUKZ.primaryLight }}>
                        <div className="h-full rounded-full transition-all" style={{ width: `${z.pct}%`, backgroundColor: z.color }} />
                      </div>
                      <span className="text-xs font-black min-w-[36px] text-right" style={{ color: z.color }}>{z.pct}%</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </WidgetCard>

          {/* Widget 3: Divergências vs Metodologia */}
          <WidgetCard icon={Shield} title="Divergências vs Metodologia" subtitle="Compliance por princípio">
            {(data.methodology_divergences || []).length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle size={24} className="mx-auto mb-2" style={{ color: VUKZ.success }} />
                <p className="text-xs font-medium" style={{ color: VUKZ.textLight }}>
                  Sem divergências — metodologia 100% aderente
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {data.methodology_divergences.map((d, i) => {
                  const color = d.compliance >= 90 ? VUKZ.success : d.compliance >= 75 ? VUKZ.warning : VUKZ.error;
                  return (
                    <div key={i} className="p-3 rounded-lg border" style={{ borderColor: VUKZ.border }}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] font-bold" style={{ color: VUKZ.text }}>{d.principle}</span>
                        <span className="text-xs font-black" style={{ color }}>{d.compliance}%</span>
                      </div>
                      <div className="h-1.5 rounded-full mb-2" style={{ backgroundColor: VUKZ.primaryLight }}>
                        <div className="h-full rounded-full" style={{ width: `${d.compliance}%`, backgroundColor: color }} />
                      </div>
                      {d.note && <p className="text-[10px]" style={{ color: VUKZ.textLight }}>{d.note}</p>}
                    </div>
                  );
                })}
              </div>
            )}
          </WidgetCard>

          {/* Widget 4: Atletas com Maior Variação */}
          <WidgetCard icon={TrendingUp} title="Variação de Atletas" subtitle="Mudanças no readiness e carga — 30 dias">
            <div className="space-y-1">
              {/* Table header */}
              <div className="grid grid-cols-12 gap-2 px-3 py-1.5 text-[9px] font-bold uppercase" style={{ color: VUKZ.textLight }}>
                <div className="col-span-1"></div>
                <div className="col-span-4">Atleta</div>
                <div className="col-span-3 text-center">Readiness Δ</div>
                <div className="col-span-3 text-center">Carga Δ</div>
                <div className="col-span-1"></div>
              </div>

              {(data.athletes_variation || []).map((a) => {
                const rdColor = a.readiness_delta.startsWith('+') ? VUKZ.success : a.readiness_delta.startsWith('-') ? VUKZ.error : VUKZ.textLight;
                return (
                  <div key={a.id} className="grid grid-cols-12 gap-2 items-center px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors">
                    <div className="col-span-1">
                      <StatusDot status={a.status} />
                    </div>
                    <div className="col-span-4">
                      <span className="text-xs font-bold" style={{ color: VUKZ.text }}>{a.name}</span>
                    </div>
                    <div className="col-span-3 text-center">
                      <span className="text-xs font-bold" style={{ color: rdColor }}>{a.readiness_delta}</span>
                    </div>
                    <div className="col-span-3 text-center">
                      <span className="text-xs font-semibold" style={{ color: VUKZ.textLight }}>{a.load_delta}</span>
                    </div>
                    <div className="col-span-1 text-right">
                      {a.status === 'improving' && <ArrowUpRight size={12} style={{ color: VUKZ.success }} />}
                      {a.status === 'declining' && <ArrowDownRight size={12} style={{ color: VUKZ.error }} />}
                      {a.status === 'stable' && <Minus size={12} style={{ color: VUKZ.textLight }} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </WidgetCard>
        </div>

        {/* Top Performers */}
        {data.top_performers && data.top_performers.length > 0 && (
          <div className="mt-6">
            <WidgetCard icon={Target} title="Top Performers" subtitle="Destaques da categoria">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {data.top_performers.map((tp, i) => (
                  <div key={tp.id} className="flex items-center gap-3 p-3 rounded-lg border" style={{ borderColor: VUKZ.border }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black" style={{ background: VUKZ_GRADIENT, color: 'white' }}>
                      {i + 1}
                    </div>
                    <div>
                      <p className="text-xs font-bold" style={{ color: VUKZ.text }}>{tp.name}</p>
                      <p className="text-[10px]" style={{ color: tp.trend === 'improving' ? VUKZ.success : tp.trend === 'declining' ? VUKZ.error : VUKZ.textLight }}>
                        {tp.delta} {tp.trend === 'improving' ? '↑' : tp.trend === 'declining' ? '↓' : '→'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </WidgetCard>
          </div>
        )}

        {/* Atletas da Categoria — clique navega para AthleteProfilePage via rota SEGURA
            /manager/roster/athlete/:athleteId (endpoints /roster/* validam membership — SEC-001).
            NOTA 07/07/2026: a bandeira histórica "NÃO usar /coach/athlete/:userId
            (SEC-A-BACKEND-SCOPE)" foi RESOLVIDA COM PROVA: o GAP GESTOR foi
            fechado (check unificado via vw_staff_accessible_athletes p/ todos
            os roles) e o smoke tests/ops/smoke_porta1_scope.py passou 8/8
            (coach/prep/nutri/gestor × in-scope 200 / out-of-scope 403,
            incluindo gestor cross-club → 403). A porta é utilizável; esta
            página segue no /roster/* por convenção do fluxo de gestão. */}
        <div className="mt-6">
          <WidgetCard
            icon={Users}
            title="Atletas"
            subtitle={
              athletesLoading
                ? 'Carregando...'
                : `${athletes.length} atleta${athletes.length !== 1 ? 's' : ''} nesta categoria`
            }
          >
            {/* Cadastro migrou da RosterPage (Fase 1) — categoryId no body faz o
                backend chamar sp_assign_athlete_category na criação: o atleta
                nasce nesta categoria. */}
            <div className="flex justify-end mb-3">
              <button
                onClick={() => setShowAddAthlete(true)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-white hover:opacity-90 transition-opacity"
                style={{ background: VUKZ_GRADIENT }}
              >
                <Plus size={14} /> Cadastrar Atleta
              </button>
            </div>
            {athletesLoading ? (
              <SkeletonRows count={4} />
            ) : athletes.length === 0 ? (
              <div className="text-center py-8">
                <Users size={24} className="mx-auto mb-2" style={{ color: VUKZ.textLight }} />
                <p className="text-xs font-medium" style={{ color: VUKZ.textLight }}>
                  Sem atletas cadastrados nesta categoria
                </p>
              </div>
            ) : (
              <div className="space-y-0.5">
                {/* Cabeçalho da lista */}
                <div
                  className="grid grid-cols-12 gap-2 px-3 py-1.5 text-[9px] font-bold uppercase tracking-wide"
                  style={{ color: VUKZ.textLight }}
                >
                  <div className="col-span-1" />
                  <div className="col-span-4">Atleta</div>
                  <div className="col-span-2 text-center">Idade</div>
                  <div className="col-span-1 text-center">Gên</div>
                  <div className="col-span-4">Treinador</div>
                </div>
                {athletes.map((a) => {
                  const age = calcAge(a.birth_date);
                  const gLabel =
                    { M: 'M', F: 'F', male: 'M', female: 'F', masculino: 'M', feminino: 'F' }[
                      a.gender
                    ] ?? '–';
                  return (
                    <Link
                      key={a.athlete_id}
                      to={`/manager/roster/athlete/${a.athlete_id}`}
                      className="grid grid-cols-12 gap-2 items-center px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      {/* Avatar por inicial */}
                      <div className="col-span-1">
                        <div
                          className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-[10px] font-black text-white"
                          style={{ background: VUKZ_GRADIENT }}
                        >
                          {getInitials(a.full_name)}
                        </div>
                      </div>
                      {/* Nome */}
                      <div className="col-span-4">
                        <span
                          className="text-xs font-semibold leading-tight block truncate"
                          style={{ color: VUKZ.text }}
                        >
                          {a.full_name}
                        </span>
                        {/* INV-5A: estado de acesso (badges; abas ficam p/ sub-bloco) */}
                        {a.account_state === 'INVITE_PENDING' && (
                          <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded border text-[9px] font-semibold"
                            style={{ borderColor: '#fde047', color: '#854d0e', backgroundColor: '#fefce8' }}>
                            ✉ Convite enviado
                            {a.pending_invite_expires_at &&
                              ` · até ${new Date(a.pending_invite_expires_at).toLocaleDateString('pt-BR')}`}
                          </span>
                        )}
                        {a.account_state === 'NO_ACCOUNT' && (
                          <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded border text-[9px] font-semibold"
                            style={{ borderColor: VUKZ.border, color: VUKZ.textLight }}>
                            Sem acesso à plataforma
                          </span>
                        )}
                      </div>
                      {/* Idade */}
                      <div className="col-span-2 text-center">
                        <span className="text-xs" style={{ color: VUKZ.textLight }}>
                          {age != null ? `${age}a` : '–'}
                        </span>
                      </div>
                      {/* Gênero */}
                      <div className="col-span-1 text-center">
                        <span className="text-[10px] font-bold" style={{ color: VUKZ.textLight }}>
                          {gLabel}
                        </span>
                      </div>
                      {/* Treinador */}
                      <div className="col-span-4">
                        <span
                          className="text-[11px] truncate block"
                          style={{ color: VUKZ.textLight }}
                        >
                          {a.coach_name || '–'}
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            )}
          </WidgetCard>
        </div>

        <AddAthleteModal
          open={showAddAthlete}
          onClose={() => setShowAddAthlete(false)}
          clubId={effectiveClubId}
          categoryId={categoryId}
          onSuccess={() => setAthletesRefresh(k => k + 1)}
        />
      </div>
    </div>
  );
};

export default CategoryDetailPage;
